import axios from 'axios';
import { shouldRefreshToken } from '@/utils/jwt';
import { authService } from '@/services/authService';
import { API_BASE_URL } from '@/config/apiBaseUrl';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout for security
});

// Request interceptor to add auth token and handle proactive refresh
apiClient.interceptors.request.use(
  async (config) => {
    // Skip token handling for login and refresh endpoints
    if (config.url?.includes('/login') || config.url?.includes('/token/refresh')) {
      return config;
    }

    let token = localStorage.getItem('auth_token');
    const refreshToken = localStorage.getItem('refresh_token');

    // Proactive token refresh: check if token needs refresh before request
    if (token && refreshToken && shouldRefreshToken(token)) {
      try {
        // Refresh token before making the request
        const response = await authService.refreshToken(refreshToken);
        token = response.token;
        
        // Update stored tokens
        localStorage.setItem('auth_token', response.token);
        if (response.refresh_token) {
          localStorage.setItem('refresh_token', response.refresh_token);
        }
      } catch (error) {
        // If refresh fails, clear tokens but don't block the request
        // The response interceptor will handle the 401
        console.warn('Proactive token refresh failed:', error);
      }
    }

    // Add token to request
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Don't set Content-Type for FormData - let axios set it automatically with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors and refresh token (reactive fallback)
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Secure token storage helpers
 */
const secureStorage = {
  setItem: (key: string, value: string) => {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error(`Failed to store ${key}:`, error);
      // If storage is full, try to clear old data
      if (error instanceof DOMException && error.code === 22) {
        console.warn('LocalStorage is full, clearing old tokens');
        localStorage.removeItem('auth_token');
        localStorage.removeItem('refresh_token');
      }
    }
  },
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error(`Failed to retrieve ${key}:`, error);
      return null;
    }
  },
  removeItem: (key: string) => {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error(`Failed to remove ${key}:`, error);
    }
  },
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Don't intercept login endpoint errors - let them be handled by the component
    if (originalRequest.url?.includes('/login') || originalRequest.url?.includes('/token/refresh')) {
      return Promise.reject(error);
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = secureStorage.getItem('refresh_token');

      if (!refreshToken) {
        // No refresh token, clear auth and redirect to login
        secureStorage.removeItem('auth_token');
        secureStorage.removeItem('refresh_token');
        processQueue(error, null);
        isRefreshing = false;
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      try {
        const response = await authService.refreshToken(refreshToken);
        const { token, refresh_token } = response;

        // Securely store new tokens
        secureStorage.setItem('auth_token', token);
        if (refresh_token) {
          secureStorage.setItem('refresh_token', refresh_token);
        }

        originalRequest.headers.Authorization = `Bearer ${token}`;
        processQueue(null, token);
        isRefreshing = false;

        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        secureStorage.removeItem('auth_token');
        secureStorage.removeItem('refresh_token');
        processQueue(refreshError, null);
        isRefreshing = false;
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);
